import {Categorie, CategorieDto} from './categorie';
import {TaakType, taakTypes} from './taak';
import {ClientDienst} from '../client/client-dienst';
import {Prestatie} from '../prestatie/prestatie';

export class Dienst {

  static deserialize(dto: DienstDto): Dienst {
    return new Dienst(
      dto.id,
      dto.label,
      dto.icoon,
      dto.categorieen.map(categorieDto => Categorie.deserialize(categorieDto)),
      dto.availableZoneIds,
    );
  }

  constructor(
    private id: string,
    private label: string,
    private icoon: string,
    private categorieen: Array<Categorie>,
    private availableZoneIds: Array<string>,
  ) {
  }

  getId(): string {
    return this.id;
  }

  hasId(id: string): boolean {
    return this.getId() === id;
  }

  getLabel(): string {
    return this.label;
  }

  getIcoon(): string {
    return this.icoon;
  }

  getCategorieen(): Array<Categorie> {
    return this.categorieen;
  }

  getCategorie(id: string): Categorie {
    return this.categorieen.find(categorie => categorie.hasId(id));
  }

  hasCategorieen(): boolean {
    return this.categorieen.length > 0;
  }

  getCategorieenByType(taakType: TaakType): Array<Categorie> {
    return this.categorieen.filter(categorie => categorie.hasTakenOfType(taakType));
  }

  getCategorieenWithStandaardTaken(): Array<Categorie> {
    return this.getCategorieenByType('STANDAARD');
  }

  getAvailableZoneIds(): Array<string> {
    return this.availableZoneIds;
  }

  hasTaken(taakType: TaakType): boolean {
    return this.getCategorieenByType(taakType).length > 0;
  }

  getTaakTypes(): Array<TaakType> {
    return taakTypes.filter(taakType => this.hasTaken(taakType));
  }

  filterForClient(clientDienst: ClientDienst): Dienst {
    return new Dienst(
      this.getId(),
      this.getLabel(),
      this.getIcoon(),
      this.getCategorieen()
        .filter(categorie => clientDienst.hasCategorie(categorie.getId()))
        .map(categorie => categorie.filterForClient(clientDienst.getCategorie(categorie.getId())))
        .filter(categorie => categorie.hasTaken()),
      this.getAvailableZoneIds(),
    );
  }

  filterOnTaakType(taakType: TaakType): Dienst {
    return new Dienst(
      this.getId(),
      this.getLabel(),
      this.getIcoon(),
      this.getCategorieen()
        .map(categorie => categorie.filterOnTaakType(taakType))
        .filter(categorie => categorie.hasTaken()),
      this.getAvailableZoneIds(),
    );
  }

  filterForPrestatie(prestatie: Prestatie, dienstId: string): Dienst {
    return new Dienst(
      this.getId(),
      this.getLabel(),
      this.getIcoon(),
      this.getCategorieen()
        .filter(categorie => prestatie.hasCategorie(dienstId, categorie.getId()))
        .map(categorie => categorie.filterForPrestatie(prestatie, dienstId, categorie.getId()))
        .filter(categorie => categorie.hasTaken()),
      this.getAvailableZoneIds(),
    );
  }
}

export interface DienstDto {
  id: string;
  label: string;
  icoon: string;
  categorieen: Array<CategorieDto>;
  availableZoneIds: Array<string>
}
